import React from 'react';

import { getAccHostname } from '@adsk/acs-sc-web-platform-api';
import { EntitlementsProject, useAlloyFeatureFlag } from '@adsk/alloy-provider';
import { navigate } from '@adsk/exo-skeleton-api';

import { LinkWithoutColor } from './LinkWithoutColor';
import { FEATURE_FLAGS } from '../../constants';
import isOpenInNewTabMouseEvent from '../../helpers/isOpenInNewTabMouseEvent';
import { getProjectUrlFallback } from '../../utils';
import {
  hasToolKey,
  parseProductAndToolKey,
} from '../../utils/defaultProductAndToolHelpers';
import { getSelectedProduct } from '../../utils/getSelectedProduct';
import { getSelectedTool } from '../../utils/getSelectedTool';
import { useProductEntryFlowContext } from '../Contexts/ProductEntryFlowContext';

export type TableLinkProps = {
  children: React.ReactNode;
  project: EntitlementsProject;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
};

export const TableLink = ({
  children,
  project,
  onClick,
  ...props
}: TableLinkProps) => {
  const { value: toolEntryFlow } = useAlloyFeatureFlag(
    FEATURE_FLAGS.ToolEntryFlow,
  );

  const { selectedProductAndToolIds } = useProductEntryFlowContext();
  const selectedProductAndToolId = selectedProductAndToolIds?.[project.id];
  let selectedProductId = selectedProductAndToolId;
  if (toolEntryFlow) {
    selectedProductId = selectedProductAndToolId
      ? parseProductAndToolKey(selectedProductAndToolId)?.productId
      : undefined;
  } else if (selectedProductAndToolId && hasToolKey(selectedProductAndToolId)) {
    // In case we opened the FF and after some time closed it, ignore the saved tool ID.
    selectedProductId = parseProductAndToolKey(
      selectedProductAndToolId,
    )?.productId;
  }
  const currentProduct = getSelectedProduct(project, selectedProductId);
  const currentTool = toolEntryFlow
    ? getSelectedTool(project, currentProduct, selectedProductAndToolId)
    : undefined;
  const url =
    currentTool?.url ||
    currentProduct?.defaultUrl ||
    project.defaultUrl ||
    getProjectUrlFallback(project);

  return (
    <LinkWithoutColor
      {...props}
      data-testid={`row-link-${project.id}`}
      href={url}
      onClick={async (event) => {
        onClick?.(event);

        if (
          !isOpenInNewTabMouseEvent(event) &&
          url.startsWith(getAccHostname())
        ) {
          event.preventDefault();
          await navigate(url);
        }
      }}
    >
      {children}
    </LinkWithoutColor>
  );
};
